// B6 (docs/build-briefs/B6-barcode-receive.md; design/mockups/b6-scan-receive.html). The scan overlay on the receiving screen:
// the camera, the match card, the Scanned list, the unknown/link/replace/discard sheets and Finish. Every rule it applies lives
// in barcodeReceiveModel.ts (tested without a DOM) and barcodeDecoder.ts; this file draws and wires them.
//
// Guarantees (each checked by e2e/b6-barcode-receive.spec.ts):
//   - Touch only. Every control is a button; there is no text field and no key handler.
//   - The camera is requested only when Scan (or Try again, or Scan more) is tapped, and every camera feed is stopped on Close camera,
//     Back, Finish, and whenever the screen is left (stop()).
//   - A code is looked up on the server; nothing is added from a guess. An unknown code adds nothing until it is linked.
//   - Replacing an existing barcode asks first, in a sheet that names the old and the new code.
//   - Nothing is posted until Finish, each row through the existing receipt command with a retry-safe idempotency key.
//   - State is shown by the frame (green or red) and text. No sound.
// Text is set with textContent only.

import {
  linkBarcode,
  listBarcodeItems,
  lookupBarcode,
  recordStockReceipt,
  type BarcodeItemDto,
  type BarcodeMatchDto,
} from "./api.ts";
import { createFrameDecoder, type FrameDecoder } from "./barcodeDecoder.ts";
import {
  HINT_TEXT,
  NOT_ON_ORDER_TEXT,
  addPicked,
  addScan,
  bottlesText,
  chipLabelForCase,
  createScanGate,
  itemCodeNote,
  itemsText,
  linkSheetView,
  linkableKinds,
  planReceipts,
  rowQuantityNote,
  setStepUnit,
  stepRow,
  type LinkFilter,
  type OrderLineInfo,
  type PlannedReceipt,
  type ScanRow,
  type StepUnit,
} from "./barcodeReceiveModel.ts";

export interface BarcodeReceiveElements {
  layer: HTMLElement;
  back: HTMLButtonElement;
  orderTag: HTMLElement;
  leftTitle: HTMLElement;
  cameraState: HTMLElement;
  closeCamera: HTMLButtonElement;
  cam: HTMLElement;
  video: HTMLVideoElement;
  facing: HTMLElement;
  flip: HTMLButtonElement;
  light: HTMLButtonElement;
  frame: HTMLElement;
  hint: HTMLElement;
  found: HTMLElement;
  foundName: HTMLElement;
  foundNote: HTMLElement;
  foundChips: HTMLElement;
  foundMinus: HTMLButtonElement;
  foundPlus: HTMLButtonElement;
  foundQty: HTMLElement;
  denied: HTMLElement;
  deniedTitle: HTMLElement;
  tryAgain: HTMLButtonElement;
  pickByHand: HTMLButtonElement;
  review: HTMLElement;
  scanMore: HTMLButtonElement;
  addByHand: HTMLButtonElement;
  count: HTMLElement;
  rows: HTMLElement;
  sum: HTMLElement;
  finish: HTMLButtonElement;
  backdrop: HTMLElement;
  unknown: HTMLElement;
  unknownCode: HTMLElement;
  unknownLink: HTMLButtonElement;
  unknownSkip: HTMLButtonElement;
  replace: HTMLElement;
  replaceItem: HTMLElement;
  replaceOld: HTMLElement;
  replaceNew: HTMLElement;
  replaceYes: HTMLButtonElement;
  replaceNo: HTMLButtonElement;
  discard: HTMLElement;
  discardCount: HTMLElement;
  discardYes: HTMLButtonElement;
  discardNo: HTMLButtonElement;
  pick: HTMLElement;
  pickTitle: HTMLElement;
  pickCancel: HTMLButtonElement;
  pickKinds: HTMLElement;
  pickLetters: HTMLElement;
  pickList: HTMLElement;
  pickCodeKinds: HTMLElement;
  pickError: HTMLElement;
  pickConfirm: HTMLButtonElement;
}

/** What the screen knows of the open order, read fresh when the overlay opens and again at Finish. */
export interface BarcodeOrderContext {
  vendorId: string;
  vendorName: string;
  lines: OrderLineInfo[];
}

export interface BarcodeReceiveDeps {
  session(): { apiBaseUrl: string; tenantId: string };
  /** Undefined when the order could not be read. */
  loadOrder(): Promise<BarcodeOrderContext | undefined>;
  /** Called once the overlay is closed after a Finish that posted something. */
  onReceived(productIds: string[], summary: string): void;
  toast(message: string, kind: "success" | "error"): void;
}

export interface BarcodeReceiveController {
  open(): Promise<void>;
  /** Stops the camera and hides the overlay, discarding the list (leaving the screen). */
  stop(): void;
  isOpen(): boolean;
}

type Mode = "scan" | "review" | "denied";
type Sheet = "none" | "unknown" | "link" | "replace" | "discard";

const FRAME_FLASH_MS = 900;
const SCAN_INTERVAL_MS = 150;
const SKIP_QUIET_MS = 2500;

interface RowError {
  [productId: string]: string;
}

export function mountBarcodeReceive(
  els: BarcodeReceiveElements,
  deps: BarcodeReceiveDeps,
): BarcodeReceiveController {
  let open = false;
  let mode: Mode = "scan";
  let sheet: Sheet = "none";
  let rows: ScanRow[] = [];
  let rowErrors: RowError = {};
  let freshProductId: string | undefined;
  let order: BarcodeOrderContext | undefined;
  let sessionId = "";
  let busy = false;
  let items: BarcodeItemDto[] | undefined;
  let unknownCode = "";
  let pickMode: "link" | "hand" = "link";
  let pickFilter: LinkFilter = { kind: null, letter: null };
  let pickSelected: string | undefined;
  let pickCodeKind: StepUnit = "bottle";
  let pendingReplace: { item: BarcodeItemDto; kind: StepUnit } | undefined;
  let frameTimer: ReturnType<typeof setTimeout> | undefined;
  // camera
  let stream: MediaStream | undefined;
  let facingMode: "environment" | "user" = "environment";
  let decoder: FrameDecoder | undefined;
  let loopTimer: ReturnType<typeof setTimeout> | undefined;
  let loopGeneration = 0;
  let gate = createScanGate();
  let torchOn = false;
  let ignoreUntil = 0;

  // ------------------------------------------------------------ drawing

  function button(
    text: string,
    className: string,
    onClick: () => void,
    label?: string,
  ): HTMLButtonElement {
    const el = document.createElement("button");
    el.type = "button";
    el.className = className;
    el.textContent = text;
    if (label !== undefined) el.setAttribute("aria-label", label);
    el.addEventListener("click", onClick);
    return el;
  }

  function setFrame(state: "idle" | "hit" | "miss"): void {
    els.frame.dataset["state"] = state;
    if (frameTimer !== undefined) clearTimeout(frameTimer);
    if (state !== "idle") {
      frameTimer = setTimeout(() => {
        els.frame.dataset["state"] = "idle";
      }, FRAME_FLASH_MS);
    }
  }

  function setHint(text: string): void {
    els.hint.textContent = text;
  }

  function renderRows(): void {
    els.rows.textContent = "";
    els.count.textContent = itemsText(rows);
    els.sum.textContent = bottlesText(rows);
    els.finish.disabled = rows.length === 0 || busy;
    if (rows.length === 0) {
      const na = document.createElement("div");
      na.className = "b6-na";
      na.textContent = "No items scanned yet. Scan a bottle to add it.";
      els.rows.append(na);
      return;
    }
    const onOrderIds = new Set((order?.lines ?? []).map((line) => line.productId));
    for (const row of rows) {
      const el = document.createElement("div");
      el.className = "b6-row";
      el.dataset["testid"] = "b6-row";
      el.dataset["productId"] = row.productId;
      if (row.productId === freshProductId) el.dataset["fresh"] = "true";
      const text = document.createElement("div");
      text.className = "b6-row-text";
      const name = document.createElement("span");
      name.dataset["testid"] = "b6-row-name";
      name.textContent = row.name;
      const sub = document.createElement("small");
      sub.textContent = rowQuantityNote(row);
      text.append(name, sub);
      const error = rowErrors[row.productId];
      if (error !== undefined) {
        const err = document.createElement("small");
        err.className = "b6-row-error";
        err.dataset["testid"] = "b6-row-error";
        err.textContent = error;
        text.append(err);
      }
      if (order !== undefined && !onOrderIds.has(row.productId)) {
        const warn = document.createElement("span");
        warn.className = "b6-tag b6-tag-warn";
        warn.dataset["testid"] = "b6-row-warning";
        warn.textContent = NOT_ON_ORDER_TEXT;
        text.append(warn);
      }
      el.append(text);
      if (mode !== "scan" || error !== undefined) {
        const mini = document.createElement("div");
        mini.className = "b6-mini";
        mini.append(
          button("−", "", () => changeRow(row.productId, -1), `Fewer ${row.name}`),
          button("+", "", () => changeRow(row.productId, 1), `More ${row.name}`),
        );
        mini.querySelectorAll("button").forEach((b, i) => {
          b.dataset["testid"] = i === 0 ? "b6-row-minus" : "b6-row-plus";
        });
        el.append(mini);
      }
      const n = document.createElement("div");
      n.className = "b6-row-n";
      n.dataset["testid"] = "b6-row-qty";
      n.textContent = String(row.bottles);
      el.append(n);
      els.rows.append(el);
    }
  }

  function renderFound(): void {
    const row = rows.find((r) => r.productId === freshProductId);
    els.found.hidden = row === undefined || mode !== "scan";
    if (row === undefined) return;
    els.foundName.textContent = row.name;
    els.foundQty.textContent = String(row.bottles);
    els.foundChips.textContent = "";
    const bottle = button("Bottle", "b6-chip", () => {
      rows = setStepUnit(rows, row.productId, "bottle");
      render();
    });
    bottle.setAttribute("aria-pressed", String(row.stepUnit === "bottle"));
    bottle.dataset["testid"] = "b6-chip-bottle";
    els.foundChips.append(bottle);
    if (row.caseSize !== null) {
      const caseChip = button(chipLabelForCase(row.caseSize), "b6-chip", () => {
        rows = setStepUnit(rows, row.productId, "case");
        render();
      });
      caseChip.setAttribute("aria-pressed", String(row.stepUnit === "case"));
      caseChip.dataset["testid"] = "b6-chip-case";
      els.foundChips.append(caseChip);
    }
  }

  function renderMode(): void {
    els.cam.hidden = mode !== "scan";
    els.denied.hidden = mode !== "denied";
    els.review.hidden = mode !== "review";
    els.closeCamera.hidden = mode !== "scan";
    els.leftTitle.textContent = mode === "review" ? "Review" : "Scan";
    els.cameraState.className =
      "b6-tag" + (mode === "scan" ? " b6-tag-ok" : mode === "denied" ? " b6-tag-warn" : "");
    els.cameraState.textContent = mode === "scan" ? "Camera on" : "Camera off";
  }

  function renderSheets(): void {
    els.backdrop.hidden = sheet === "none";
    els.unknown.hidden = sheet !== "unknown";
    els.replace.hidden = sheet !== "replace";
    els.discard.hidden = sheet !== "discard";
    els.pick.hidden = sheet !== "link";
  }

  function render(): void {
    renderMode();
    renderRows();
    renderFound();
    renderSheets();
  }

  function changeRow(productId: string, direction: 1 | -1): void {
    rows = stepRow(rows, productId, direction);
    delete rowErrors[productId];
    if (!rows.some((r) => r.productId === freshProductId)) freshProductId = undefined;
    render();
  }

  // ------------------------------------------------------------ camera

  function stopStream(): void {
    loopGeneration += 1;
    if (loopTimer !== undefined) clearTimeout(loopTimer);
    loopTimer = undefined;
    if (stream !== undefined) {
      for (const feed of stream.getTracks()) feed.stop();
      stream = undefined;
    }
    els.video.srcObject = null;
    torchOn = false;
  }

  async function startCamera(): Promise<void> {
    stopStream();
    mode = "scan";
    sheet = "none";
    els.light.hidden = true;
    setHint(HINT_TEXT);
    render();
    const media = navigator.mediaDevices;
    if (media === undefined || typeof media.getUserMedia !== "function") {
      showDenied("No camera found", false);
      return;
    }
    const generation = loopGeneration;
    try {
      const next = await media.getUserMedia({
        video: { facingMode: { ideal: facingMode } },
        audio: false,
      });
      if (!open || generation !== loopGeneration) {
        for (const feed of next.getTracks()) feed.stop();
        return;
      }
      stream = next;
      els.video.srcObject = next;
      await els.video.play().catch(() => undefined);
    } catch (error) {
      if (!open || generation !== loopGeneration) return;
      const name = (error as { name?: string }).name ?? "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        showDenied("Camera is blocked", true);
      } else {
        showDenied("No camera found", false);
      }
      return;
    }
    const feed = stream.getVideoTracks()[0];
    const settings = feed?.getSettings() ?? {};
    els.facing.textContent =
      settings.facingMode === "user" || facingMode === "user" ? "Front camera" : "Rear camera";
    const capabilities = (feed?.getCapabilities?.() ?? {}) as { torch?: boolean };
    els.light.hidden = capabilities.torch !== true;
    els.light.textContent = "Light off";
    void media
      .enumerateDevices()
      .then((devices) => {
        els.flip.hidden = devices.filter((d) => d.kind === "videoinput").length < 2;
      })
      .catch(() => {
        els.flip.hidden = true;
      });
    if (decoder === undefined) {
      decoder = await createFrameDecoder(
        window as unknown as Parameters<typeof createFrameDecoder>[0],
        () => {
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d", { willReadFrequently: true });
          if (context === null) throw new Error("canvas 2d context unavailable");
          return { canvas, context };
        },
      );
    }
    gate = createScanGate();
    scheduleScan(generation);
  }

  function showDenied(title: string, canTryAgain: boolean): void {
    stopStream();
    mode = "denied";
    els.deniedTitle.textContent = title;
    els.tryAgain.hidden = !canTryAgain;
    render();
  }

  function scheduleScan(generation: number): void {
    loopTimer = setTimeout(() => void scanTick(generation), SCAN_INTERVAL_MS);
  }

  async function scanTick(generation: number): Promise<void> {
    if (!open || generation !== loopGeneration || decoder === undefined) return;
    if (
      sheet === "none" &&
      mode === "scan" &&
      els.video.readyState >= 2 &&
      Date.now() >= ignoreUntil
    ) {
      let code: string | null = null;
      try {
        code = await decoder.decode(els.video);
      } catch (error) {
        console.error("barcode decode failed", error);
      }
      if (generation !== loopGeneration) return;
      if (code !== null && gate.accept(code, Date.now())) {
        await handleCode(code);
      }
    }
    if (generation === loopGeneration) scheduleScan(generation);
  }

  async function handleCode(code: string): Promise<void> {
    const session = deps.session();
    const result = await lookupBarcode(session.apiBaseUrl, session.tenantId, code);
    if (!open) return;
    if (result.status !== 200) {
      setFrame("miss");
      setHint(result.body.message ?? "Could not check this barcode. Check your connection.");
      return;
    }
    const match = result.body.match;
    if (match === null) {
      setFrame("miss");
      unknownCode = code;
      els.unknownCode.textContent = code;
      sheet = "unknown";
      render();
      return;
    }
    applyMatch(match);
  }

  function applyMatch(match: BarcodeMatchDto): void {
    const added = addScan(rows, match);
    if (added.refused !== undefined) {
      setFrame("miss");
      setHint(`${match.name}: ${added.refused}`);
      return;
    }
    rows = added.rows;
    freshProductId = match.liquorProductId;
    delete rowErrors[match.liquorProductId];
    setHint("");
    setFrame("hit");
    render();
  }

  // ------------------------------------------------------------ link and pick sheets

  async function loadItems(): Promise<boolean> {
    const session = deps.session();
    const result = await listBarcodeItems(session.apiBaseUrl, session.tenantId);
    if (result.status !== 200) {
      els.pickError.hidden = false;
      els.pickError.textContent =
        result.body.message ?? "Could not load the items. Check your connection.";
      return false;
    }
    items = result.body.items;
    return true;
  }

  async function openPick(kind: "link" | "hand"): Promise<void> {
    pickMode = kind;
    pickFilter = { kind: null, letter: null };
    pickSelected = undefined;
    pickCodeKind = "bottle";
    els.pickError.hidden = true;
    els.pickTitle.textContent = kind === "link" ? `Link ${unknownCode} to` : "Pick an item";
    sheet = "link";
    renderSheets();
    renderPick();
    if (items === undefined) {
      await loadItems();
      renderPick();
    }
  }

  function renderPick(): void {
    const all = items ?? [];
    const view = linkSheetView(all, pickFilter);
    els.pickKinds.textContent = "";
    for (const kind of view.kinds) {
      const chip = button(kind.label, "b6-chip", () => {
        pickFilter = { kind: kind.value, letter: null };
        renderPick();
      });
      chip.setAttribute("aria-pressed", String(pickFilter.kind === kind.value));
      els.pickKinds.append(chip);
    }
    els.pickLetters.textContent = "";
    for (const letter of view.letters) {
      const b = button(letter, "", () => {
        pickFilter = { ...pickFilter, letter: pickFilter.letter === letter ? null : letter };
        renderPick();
      });
      b.setAttribute("aria-pressed", String(pickFilter.letter === letter));
      els.pickLetters.append(b);
    }
    els.pickList.textContent = "";
    for (const item of view.items) {
      const option = document.createElement("button");
      option.type = "button";
      option.className = "b6-option";
      option.dataset["testid"] = "b6-pick-option";
      option.setAttribute("aria-pressed", String(pickSelected === item.id));
      const name = document.createElement("b");
      name.textContent = item.name;
      option.append(name);
      if (pickMode === "link") {
        const note = document.createElement("small");
        note.textContent = itemCodeNote(item, "bottle");
        option.append(note);
      }
      option.addEventListener("click", () => {
        pickSelected = item.id;
        if (!linkableKinds(item).includes(pickCodeKind)) pickCodeKind = "bottle";
        els.pickError.hidden = true;
        renderPick();
      });
      els.pickList.append(option);
    }
    const selected = all.find((item) => item.id === pickSelected);
    els.pickCodeKinds.textContent = "";
    els.pickCodeKinds.hidden =
      pickMode !== "link" || selected === undefined || linkableKinds(selected).length < 2;
    if (selected !== undefined && !els.pickCodeKinds.hidden) {
      for (const kind of linkableKinds(selected)) {
        const chip = button(
          kind === "bottle" ? "Bottle barcode" : "Case barcode",
          "b6-chip",
          () => {
            pickCodeKind = kind;
            renderPick();
          },
        );
        chip.setAttribute("aria-pressed", String(pickCodeKind === kind));
        chip.dataset["testid"] = kind === "bottle" ? "b6-codekind-bottle" : "b6-codekind-case";
        els.pickCodeKinds.append(chip);
      }
    }
    els.pickConfirm.disabled = selected === undefined;
    if (pickMode === "hand") {
      els.pickConfirm.textContent = "Add 1";
    } else if (pickCodeKind === "case" && selected?.caseSize !== null && selected !== undefined) {
      els.pickConfirm.textContent = `Link and add ${chipLabelForCase(selected.caseSize ?? 0).toLowerCase()}`;
    } else {
      els.pickConfirm.textContent = "Link and add 1";
    }
  }

  async function confirmPick(): Promise<void> {
    const item = (items ?? []).find((candidate) => candidate.id === pickSelected);
    if (item === undefined) return;
    if (pickMode === "hand") {
      rows = addPicked(rows, item);
      freshProductId = item.id;
      sheet = "none";
      render();
      return;
    }
    const existing = pickCodeKind === "bottle" ? item.barcode : item.caseBarcode;
    if (existing !== null && existing !== unknownCode) {
      pendingReplace = { item, kind: pickCodeKind };
      els.replaceItem.textContent = item.name;
      els.replaceOld.textContent = existing;
      els.replaceNew.textContent = unknownCode;
      sheet = "replace";
      renderSheets();
      return;
    }
    await doLink(item, pickCodeKind, false);
  }

  async function doLink(item: BarcodeItemDto, kind: StepUnit, replace: boolean): Promise<void> {
    const session = deps.session();
    els.pickConfirm.disabled = true;
    const result = await linkBarcode(session.apiBaseUrl, session.tenantId, {
      liquorProductId: item.id,
      code: unknownCode,
      kind,
      replace,
    });
    if (!open) return;
    if (result.status !== 200) {
      // Back on the item list with the server's own words; nothing was linked.
      sheet = "link";
      renderSheets();
      els.pickError.hidden = false;
      els.pickError.textContent =
        result.body.message ?? "Could not link this barcode. Check your connection.";
      renderPick();
      return;
    }
    const updated: BarcodeItemDto = {
      ...item,
      ...(kind === "bottle" ? { barcode: unknownCode } : { caseBarcode: unknownCode }),
    };
    items = (items ?? []).map((candidate) => (candidate.id === item.id ? updated : candidate));
    pendingReplace = undefined;
    sheet = "none";
    applyMatch({
      liquorProductId: item.id,
      name: item.name,
      itemKind: item.itemKind,
      active: true,
      matchedBy: kind,
      receiveAs: item.receiveAs,
      caseSize: item.caseSize,
    });
    render();
  }

  // ------------------------------------------------------------ finish

  async function finish(): Promise<void> {
    if (busy || rows.length === 0) return;
    busy = true;
    rowErrors = {};
    renderRows();
    const fresh = await deps.loadOrder();
    if (fresh === undefined) {
      busy = false;
      deps.toast("Could not read the order. Check your connection.", "error");
      renderRows();
      return;
    }
    order = fresh;
    const session = deps.session();
    const plan: PlannedReceipt[] = planReceipts(rows, fresh.lines, fresh, sessionId);
    const done: string[] = [];
    let bottlesPosted = 0;
    for (const planned of plan) {
      if (planned.body === undefined) {
        rowErrors[planned.productId] = planned.error ?? "Could not post this row";
        continue;
      }
      const result = await recordStockReceipt(
        session.apiBaseUrl,
        session.tenantId,
        planned.body as unknown as Parameters<typeof recordStockReceipt>[2],
      );
      if (result.status === 200 || result.status === 201) {
        done.push(planned.productId);
        bottlesPosted += planned.bottles;
      } else {
        rowErrors[planned.productId] =
          result.body.message ?? "Could not record this row. Check your connection.";
      }
    }
    rows = rows.filter((row) => !done.includes(row.productId));
    busy = false;
    if (rows.length === 0) {
      const summary = `Received ${bottlesPosted} ${bottlesPosted === 1 ? "bottle" : "bottles"}.`;
      close();
      deps.onReceived(done, summary);
      return;
    }
    if (done.length > 0) {
      deps.toast(
        `Received ${bottlesPosted} ${bottlesPosted === 1 ? "bottle" : "bottles"}. Some rows need attention.`,
        "error",
      );
      deps.onReceived(done, "");
    }
    render();
  }

  // ------------------------------------------------------------ open and close

  function close(): void {
    stopStream();
    open = false;
    rows = [];
    rowErrors = {};
    freshProductId = undefined;
    sheet = "none";
    els.layer.hidden = true;
    if (frameTimer !== undefined) clearTimeout(frameTimer);
  }

  async function openOverlay(): Promise<void> {
    const loaded = await deps.loadOrder();
    if (loaded === undefined) {
      deps.toast("Could not read the order. Check your connection.", "error");
      return;
    }
    order = loaded;
    rows = [];
    rowErrors = {};
    items = undefined;
    freshProductId = undefined;
    busy = false;
    sessionId = crypto.randomUUID().replace(/-/g, "").slice(0, 12);
    els.orderTag.textContent = loaded.vendorName;
    open = true;
    els.layer.hidden = false;
    els.found.hidden = true;
    await startCamera();
  }

  // ------------------------------------------------------------ wiring

  els.back.addEventListener("click", () => {
    if (rows.length === 0) {
      close();
      return;
    }
    els.discardCount.textContent = `${itemsText(rows)}, ${bottlesText(rows)}`;
    sheet = "discard";
    renderSheets();
  });
  els.discardYes.addEventListener("click", close);
  els.discardNo.addEventListener("click", () => {
    sheet = "none";
    renderSheets();
  });
  els.closeCamera.addEventListener("click", () => {
    stopStream();
    mode = "review";
    sheet = "none";
    render();
  });
  els.scanMore.addEventListener("click", () => void startCamera());
  els.tryAgain.addEventListener("click", () => void startCamera());
  els.pickByHand.addEventListener("click", () => void openPick("hand"));
  els.addByHand.addEventListener("click", () => void openPick("hand"));
  els.flip.addEventListener("click", () => {
    facingMode = facingMode === "environment" ? "user" : "environment";
    void startCamera();
  });
  els.light.addEventListener("click", () => {
    const feed = stream?.getVideoTracks()[0];
    if (feed === undefined) return;
    torchOn = !torchOn;
    void feed
      .applyConstraints({ advanced: [{ torch: torchOn } as MediaTrackConstraintSet] })
      .then(() => {
        els.light.textContent = torchOn ? "Light on" : "Light off";
      })
      .catch(() => {
        torchOn = false;
        els.light.hidden = true;
      });
  });
  els.foundMinus.addEventListener("click", () => {
    if (freshProductId !== undefined) changeRow(freshProductId, -1);
  });
  els.foundPlus.addEventListener("click", () => {
    if (freshProductId !== undefined) changeRow(freshProductId, 1);
  });
  els.unknownSkip.addEventListener("click", () => {
    // The code is probably still in front of the camera; do not ask about it again at once.
    ignoreUntil = Date.now() + SKIP_QUIET_MS;
    sheet = "none";
    renderSheets();
  });
  els.unknownLink.addEventListener("click", () => void openPick("link"));
  els.pickCancel.addEventListener("click", () => {
    sheet = pickMode === "link" ? "unknown" : "none";
    renderSheets();
  });
  els.pickConfirm.addEventListener("click", () => void confirmPick());
  els.replaceNo.addEventListener("click", () => {
    pendingReplace = undefined;
    sheet = "link";
    renderSheets();
  });
  els.replaceYes.addEventListener("click", () => {
    const pending = pendingReplace;
    if (pending !== undefined) void doLink(pending.item, pending.kind, true);
  });
  els.finish.addEventListener("click", () => void finish());

  return {
    open: openOverlay,
    stop: close,
    isOpen: () => open,
  };
}
